import { useState, useRef, useMemo, useCallback, useEffect, ChangeEvent } from 'react';
import { useAsync } from 'react-use';
import { cx } from '@linaria/core';
import { useMenuState, MenuButton, Menu, MenuItem } from 'reakit/Menu';
import { Input as InputComponent } from 'components/Input';
import { Spinner } from 'components/Spinner/Spinner';
import { Concept, Query, Search, Filter, Rank, Annotation, Data, Input } from 'api/grpc/resources';
import { useGRPCClient } from 'api/grpc/client';
import { searchConcepts, listAllConcepts } from 'components/ConceptSelector/helpers';
import { autocompleteWrapper, autocompleteInput, conceptName, autocompleteMenu, conceptNameItem } from 'components/ConceptSelector/styles';
import { Button } from 'components/ui_library/Button';
import { IconCheckThin } from 'components/Icons';
import { fetchInputsCountsSearches } from 'api/inputs/fetchInputsCounts';
import { getDatasetVersionMetricsTE } from 'api/datasets/getDatasetVersionMetrics';
import { useFormikContext } from 'formik';
import { FormikInitialValues } from 'modules/Models/components/ModelForm/utils/types';
import { DATASET_ID_PATH, DATASET_VERSION_ID_PATH } from 'modules/Models/components/ModelForm/utils/utils';
import { either, pipe, errorToReactLeft } from 'utils/fp';
import { useQueryTE } from 'utils/react-query';

const DEFAULT_CONCEPT_COUNT = 50;

export interface ConceptAutocompleteProps {
  id?: string;
  userOrOrgId: string;
  appId: string;
  includeMainAppConcepts?: boolean;
  selectedConcepts: Concept[];
  labeledConcepts: string[];
  onConceptSelected: (concept: Concept[]) => void;
  onConceptCreated: (concept: string) => void;
  renderEmptyPlaceholder?: (concept: string) => JSX.Element;
  isDisabled?: boolean;
  className?: string;
  showSelectAll?: boolean;
}

/**
 * Shows Input and Menu
 * Enables user to search, select and create concepts
 *
 * TODO: Refactor this component. It is too big and has too many responsibilities and hence needs to be split
 * into smaller components.
 *
 * What this component does:
 * We either display all concepts OR only the ones that are labeled in the dataset version IF the dataset version is selected.
 * If we display all concepts, we need to fetch the counts for each concept. To complicated things further,
 * we need to fetch the counts only for the concepts that are displayed in the current page of the paginated concepts list.
 */
export function ConceptAutocomplete(props: ConceptAutocompleteProps): JSX.Element | null {
  const {
    appId,
    className,
    includeMainAppConcepts = false,
    isDisabled,
    labeledConcepts,
    onConceptSelected,
    selectedConcepts,
    showSelectAll = true,
    userOrOrgId,
  } = props;
  const client = useGRPCClient();
  const [filterText, setFilterText] = useState('');
  const [isLoadMoreEnabled, setLoadMoreEnabled] = useState(false);
  const [isSelectAllClicked, setSelectAllClicked] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [conceptCountMap, setConceptCountMap] = useState<{ [key: string]: number }>({});
  const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_CONCEPT_COUNT);

  const { values } = useFormikContext();
  const selectedDatasetId = (values as FormikInitialValues).fieldValueMap[DATASET_ID_PATH] as string;
  const selectedDatasetVersionId = (values as FormikInitialValues).fieldValueMap[DATASET_VERSION_ID_PATH] as string;
  const menu = useMenuState({ placement: 'bottom-start' });

  const queryFn = useCallback(
    (query) => {
      if (query) {
        return searchConcepts(client, { userOrOrgId, appId, includeMainAppConcepts })(query);
      }

      return listAllConcepts(client, userOrOrgId, appId, includeMainAppConcepts)();
    },
    [client, userOrOrgId, appId, includeMainAppConcepts],
  );
  const { loading, error, value: concepts = [] } = useAsync(() => queryFn(filterText), [filterText]);
  const [paginatedConcepts, setPaginatedConcepts] = useState<Concept[]>([]);
  const [datasetVersionConcepts, setdatasetVersionConcepts] = useState<Concept[]>([]);

  const { labeledConceptCountMap } = useMemo(() => {
    const labeledConceptCount: Record<string, number> = {};
    let labeledCount = 0;
    concepts.forEach((concept) => {
      labeledConceptCount[concept.getId()] = 0;
    });
    labeledConcepts.forEach((concept) => {
      labeledConceptCount[concept] += 1;
      if (labeledConceptCount[concept]) {
        labeledCount += 1;
      }
    });
    return { labeledConceptCountMap: labeledConceptCount, totalLabeledCount: labeledCount };
  }, [labeledConcepts, concepts]);

  useEffect(() => {
    if (menu.visible) {
      inputRef.current?.focus();
    }
  }, [menu, concepts]);

  const handleInputChanged = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
  }, []);

  const loadMoreClickHandler = useCallback(() => {
    const newPageSize = currentPageSize + DEFAULT_CONCEPT_COUNT;
    if (concepts.length <= newPageSize) {
      setLoadMoreEnabled(false);
      setPaginatedConcepts(concepts);
      setCurrentPageSize(concepts.length);
    } else {
      setLoadMoreEnabled(true);
      setPaginatedConcepts(concepts.slice(0, newPageSize));
      setCurrentPageSize(newPageSize);
    }
  }, [concepts]);

  const handleConceptClick = useCallback(
    (concept) => {
      const isSelected = selectedConcepts.find((selectedConcept) => selectedConcept.getName() === concept.getName());
      if (isSelected) {
        onConceptSelected(selectedConcepts.filter((selectedConcept) => selectedConcept.getName() !== concept.getName()));
        setSelectAllClicked(false);
      } else {
        onConceptSelected([...selectedConcepts, concept]);
      }
    },
    [selectedConcepts, labeledConceptCountMap],
  );

  const fetchCounts = useCallback(async () => {
    const conceptSubset = paginatedConcepts.slice(-DEFAULT_CONCEPT_COUNT);
    const searches = conceptSubset.map((concept) => {
      const search = new Search();
      const query = new Query();

      const filter = new Filter();
      const annotation = new Annotation();
      const annotationData = new Data();

      const conceptObj = new Concept();
      conceptObj.setId(concept.getId());
      conceptObj.setName(concept.getName());
      conceptObj.setValue(1);

      annotationData.setConceptsList([conceptObj]);
      annotation.setData(annotationData);

      if (selectedDatasetId) {
        const datasetFilter = new Input();
        datasetFilter.setDatasetIdsList([selectedDatasetId]);
        filter.setInput(datasetFilter);
      }

      filter.setAnnotation(annotation);
      const ranks: Rank[] = [];
      query.setFiltersList([filter]);
      query.setRanksList(ranks);
      search.setQuery(query);

      return search;
    });

    const resp = await fetchInputsCountsSearches({ userOrOrgId, appId, searches });
    const hitCounts = resp.hit_counts;
    const labelConceptCountMap = conceptSubset.reduce((acc, concept, index) => {
      const estimatedCount = Number.parseInt(hitCounts[index].estimated_total, 10);
      acc[concept.getId()] = estimatedCount || 0;
      return acc;
    }, {} as { [key: string]: number });
    setConceptCountMap({ ...conceptCountMap, ...labelConceptCountMap });
  }, [userOrOrgId, appId, paginatedConcepts, selectedDatasetId]);

  const { data: datasetVersionMetrics10 } = useQueryTE(
    [
      'DatasetVersionMetrics',
      {
        appId,
        userId: userOrOrgId,
        datasetId: selectedDatasetId,
        datasetVersionId: selectedDatasetVersionId,
        typeId: 10,
      },
    ],
    getDatasetVersionMetricsTE(
      {
        appId,
        userId: userOrOrgId,
        datasetId: selectedDatasetId,
        datasetVersionId: selectedDatasetVersionId,
        typeId: 10, // type 10 is for concepts only
      },
      errorToReactLeft,
    ),
    {
      enabled: Boolean(userOrOrgId) && Boolean(selectedDatasetId) && Boolean(selectedDatasetVersionId),
    },
  );
  const transformVersionMetrics = useCallback((versionMetricsGroups: CF.API.Datasets.DatasetVersionMetricsGroup[]) => {
    const conceptMap: { [key: string]: number } = {};
    const versionConcepts = versionMetricsGroups.map((group) => {
      const newConcept = new Concept();
      newConcept.setId(group.value);
      newConcept.setName(group.value);
      const conceptCount = Number.parseInt(group.metrics.inputs_count, 10);
      conceptMap[group.value] = conceptCount;
      return newConcept;
    });
    setConceptCountMap(conceptMap);
    setdatasetVersionConcepts(versionConcepts);
  }, []);

  useEffect(() => {
    if (datasetVersionMetrics10) {
      pipe(
        datasetVersionMetrics10,
        either.fold(
          () => {},
          (resp) => transformVersionMetrics(resp.dataset_version_metrics_groups),
        ),
      );
    }
  }, [datasetVersionMetrics10]);

  useEffect(() => {
    const selectconcepts = datasetVersionConcepts;
    const isPaginationNeeded = selectconcepts.length > DEFAULT_CONCEPT_COUNT;
    setLoadMoreEnabled(isPaginationNeeded);
    setPaginatedConcepts(isPaginationNeeded ? selectconcepts.slice(0, DEFAULT_CONCEPT_COUNT) : selectconcepts);
  }, [concepts.length, datasetVersionConcepts.length]);

  useEffect(() => {
    if (paginatedConcepts.length) {
      fetchCounts();
    }
  }, [paginatedConcepts.length, selectedDatasetId]);

  const transformedConcepts = useMemo(() => {
    const filtered = paginatedConcepts.filter((concept) => concept.getName().toLowerCase().includes(filterText.toLowerCase()));
    return filtered.sort((a, b) => a.getName().toLowerCase().localeCompare(b.getName().toLowerCase()));
  }, [paginatedConcepts, filterText]);

  const filteredLabeledConceptCount = useMemo(() => {
    return transformedConcepts.filter((c) => {
      const key = c.getId() || c.getName();
      return conceptCountMap[key] > 0;
    }).length;
  }, [transformedConcepts, conceptCountMap]);

  const selectAlllabeledConcepts = useCallback(() => {
    if (isSelectAllClicked) {
      onConceptSelected([]);
    } else {
      const labeledOnlyConcepts = transformedConcepts.filter((c) => {
        const key = c.getId() || c.getName();
        return conceptCountMap[key] > 0;
      });
      onConceptSelected(labeledOnlyConcepts);
    }
    setSelectAllClicked(!isSelectAllClicked);
  }, [transformedConcepts, isSelectAllClicked, conceptCountMap]);

  return (
    <div className={cx(autocompleteWrapper, className)}>
      <MenuButton {...menu} className="autocomplete-btn" disabled={isDisabled || !transformedConcepts.length}>
        <InputComponent
          ref={inputRef}
          role="textbox"
          className={cx(autocompleteInput, menu.visible && 'drawer-open', isDisabled && 'disabled')}
          value={filterText}
          onChange={handleInputChanged}
          aria-label="Select concepts"
          placeholder={transformedConcepts.length ? 'Select concepts' : 'The list of concepts is empty...'}
        />
      </MenuButton>
      <Menu {...menu} aria-label="Search concepts" className={autocompleteMenu}>
        {loading && <Spinner />}
        {!loading && error && <div>Sorry, there was an error</div>}
        {!loading && (
          <>
            {showSelectAll && (
              <div className={cx('select-all', isSelectAllClicked && 'selected')}>
                <MenuItem
                  {...menu}
                  aria-labelledby="select-all-labeled-concepts"
                  data-testid="select-all-labeled-concepts"
                  onClick={selectAlllabeledConcepts}
                >
                  <div className="select-all-data">
                    <div>Select all labeled ({filteredLabeledConceptCount})</div>
                    {isSelectAllClicked && (
                      <div className="check">
                        <IconCheckThin size={23} color="#195AFF" />
                      </div>
                    )}
                  </div>
                </MenuItem>
              </div>
            )}
            {transformedConcepts.map((concept: Concept) => {
              const isSelected = selectedConcepts.find(
                (selectedConcept) => selectedConcept.getName() === concept.getName() || selectedConcept.getId() === concept.getId(),
              );

              return (
                <MenuItem
                  {...menu}
                  key={concept.getId()}
                  aria-labelledby={`${concept.getName()} Concept`}
                  data-testid={`auto-complete-result-${concept.getName()}`}
                  className={cx(conceptNameItem, isSelected && 'selected')}
                  onClick={() => handleConceptClick(concept)}
                >
                  <div className="concept-data">
                    <div>{concept.getName()}</div>
                    <div className="label-count">(~{conceptCountMap[concept.getName() || concept.getId()]} labeled)</div>
                  </div>
                  {isSelected && (
                    <div className="check">
                      <IconCheckThin size={23} color="#195AFF" />
                    </div>
                  )}
                </MenuItem>
              );
            })}
            {isLoadMoreEnabled && concepts.length > paginatedConcepts.length && (
              <div className={conceptNameItem}>
                <Button variant="link" onClick={loadMoreClickHandler}>
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
        {
          /* When text entered yields no results */ !loading && Boolean(filterText.length) && concepts.length === 0 && (
            <MenuItem {...menu} className={conceptName} onClick={() => {}}>
              No results
            </MenuItem>
          )
        }
      </Menu>
    </div>
  );
}
